import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Copy,
  Loader2,
  PackageCheck,
  RefreshCw,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Btn, Card, FieldLabel, inputCls } from "@/components/admin/ui";

const emptyOrigin = {
  name: "SPICY3D WORLD",
  company: "",
  street1: "",
  street2: "",
  city: "",
  state: "FL",
  zip: "",
  country: "US",
};
const emptyPackage = {
  name: "",
  package_type: "padded_mailer",
  length: "",
  width: "",
  height: "",
  weight: "",
};

const packageFields = (values, setValues, prefix) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
    <label>
      <FieldLabel>Packaging type</FieldLabel>
      <select
        className={inputCls}
        value={values.package_type}
        onChange={(event) =>
          setValues({ ...values, package_type: event.target.value })
        }
        data-testid={`${prefix}-package-type`}
      >
        <option value="padded_mailer">Padded poly mailer</option>
        <option value="cardboard_box">Cardboard box</option>
        <option value="custom">Custom packaging</option>
      </select>
    </label>
    {[
      ["length", "Length (in)"],
      ["width", "Width (in)"],
      ["height", "Height (in)"],
      ["weight", "Weight (lb)"],
    ].map(([field, label]) => (
      <label key={field}>
        <FieldLabel>{label}</FieldLabel>
        <input
          type="number"
          min="0"
          step="0.01"
          className={inputCls}
          value={values[field] || ""}
          onChange={(event) =>
            setValues({ ...values, [field]: event.target.value })
          }
          data-testid={`${prefix}-${field}`}
        />
      </label>
    ))}
  </div>
);

export default function ShippoShipping() {
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connection, setConnection] = useState(null);
  const [preset, setPreset] = useState(emptyPackage);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [profile, setProfile] = useState({ ...emptyPackage, name: "" });

  const load = useCallback(async () => {
    const [configRes, productRes] = await Promise.all([
      api.get("/admin/shippo/config"),
      api.get("/admin/shippo/products"),
    ]);
    setConfig({
      ...configRes.data,
      origin: { ...emptyOrigin, ...(configRes.data.origin || {}) },
    });
    setProducts(productRes.data);
    if (!selectedProduct && productRes.data.length) {
      const first = productRes.data[0];
      setSelectedProduct(first.id);
      setProfile({
        ...emptyPackage,
        ...(first.shippo_shipping || {}),
        name: "",
      });
    }
  }, [selectedProduct]);

  useEffect(() => {
    load().catch((error) => toast.error(errMsg(error)));
  }, [load]);

  const selected = useMemo(
    () => products.find((product) => product.id === selectedProduct),
    [products, selectedProduct],
  );

  const updateConfig = (patch) =>
    setConfig((current) => ({ ...current, ...patch }));
  const updateOrigin = (field, value) =>
    updateConfig({ origin: { ...config.origin, [field]: value } });

  const saveConfig = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/shippo/config", {
        enabled: config.enabled,
        live_rates_enabled: config.live_rates_enabled,
        fallback_rate: Number(config.fallback_rate),
        carriers: config.carriers,
        origin: config.origin,
        default_preset_id: config.default_preset_id,
      });
      setConfig(data);
      toast.success("Shippo shipping settings saved.");
    } catch (error) {
      toast.error(errMsg(error));
    }
    setSaving(false);
  };

  const testConnection = async () => {
    setTesting(true);
    setConnection(null);
    try {
      const { data } = await api.post("/admin/shippo/connection-test");
      setConnection(data);
      toast.success("Shippo LIVE connection verified.");
    } catch (error) {
      toast.error(errMsg(error));
    }
    setTesting(false);
  };

  const savePreset = async () => {
    try {
      await api.post("/admin/shippo/presets", {
        ...preset,
        length: Number(preset.length),
        width: Number(preset.width),
        height: Number(preset.height),
        weight: Number(preset.weight),
      });
      setPreset(emptyPackage);
      await load();
      toast.success("Package preset saved.");
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const saveProfile = async () => {
    if (!selected) return;
    try {
      await api.put(`/admin/shippo/products/${selected.id}`, {
        preset_id: profile.preset_id || "",
        package_type: profile.package_type,
        length: Number(profile.length),
        width: Number(profile.width),
        height: Number(profile.height),
        weight: Number(profile.weight),
      });
      await load();
      toast.success("Product shipping profile saved.");
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const copyWebhook = async () => {
    await navigator.clipboard.writeText(config.webhook_url);
    toast.success("Webhook URL copied.");
  };

  if (!config) {
    return (
      <div className="py-20 text-center text-zinc-400">
        <Loader2 className="animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shippo-shipping-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">
            Settings · Shipping
          </p>
          <h1 className="text-2xl font-bold mt-1">Shippo</h1>
        </div>
        <button
          type="button"
          onClick={() => load().catch((error) => toast.error(errMsg(error)))}
          className="p-2 text-zinc-500 hover:text-zinc-950"
          data-testid="shippo-refresh-button"
          aria-label="Refresh Shippo settings"
        >
          <RefreshCw size={17} />
        </button>
      </div>

      <Card
        title="Shippo LIVE connection"
        description="Token is stored only in protected server secrets."
      >
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`text-xs font-medium px-2.5 py-1 rounded-full border ${
              config.token_configured
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-100 text-zinc-600 border-zinc-200"
            }`}
            data-testid="shippo-connection-status"
          >
            {config.token_configured ? "Token configured" : "Disconnected"}
          </span>
          <Btn
            type="button"
            variant="secondary"
            onClick={testConnection}
            disabled={testing || !config.token_configured}
            data-testid="shippo-test-connection-button"
          >
            {testing && <Loader2 size={15} className="animate-spin" />} Test
            connection
          </Btn>
          {connection?.connected && (
            <span className="text-sm text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 size={15} />{" "}
              {connection.available_carriers.join(", ") ||
                "Connection verified"}
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-500 mt-3">
          To replace the token, use the protected production secret named{" "}
          <strong>SHIPPO_API_TOKEN</strong> and republish. The token is never
          returned to this page.
        </p>
      </Card>

      <Card
        title="Shipping origin"
        description="Required before live checkout rates can be enabled."
      >
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            ["name", "Sender name"],
            ["company", "Company"],
            ["street1", "Street address"],
            ["street2", "Unit / suite"],
            ["city", "City"],
            ["state", "State"],
            ["zip", "ZIP code"],
            ["country", "Country code"],
          ].map(([field, label]) => (
            <label
              key={field}
              className={field === "street1" ? "sm:col-span-2" : ""}
            >
              <FieldLabel>{label}</FieldLabel>
              <input
                className={inputCls}
                value={config.origin?.[field] || ""}
                onChange={(event) => updateOrigin(field, event.target.value)}
                data-testid={`shippo-origin-${field}`}
              />
            </label>
          ))}
        </div>
      </Card>

      <Card
        title="Checkout rates"
        description="Live rates replace the current fallback only after you enable them here."
      >
        <div className="space-y-4">
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(event) =>
                updateConfig({ enabled: event.target.checked })
              }
              data-testid="shippo-enabled-toggle"
            />{" "}
            Enable Shippo settings
          </label>
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={config.live_rates_enabled}
              onChange={(event) =>
                updateConfig({ live_rates_enabled: event.target.checked })
              }
              data-testid="shippo-live-rates-toggle"
            />{" "}
            Enable live USPS and UPS rates at checkout
          </label>
          <div className="flex flex-wrap gap-4 text-sm">
            <span>Carriers:</span>
            {["USPS", "UPS"].map((carrier) => (
              <label key={carrier} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.carriers.includes(carrier)}
                  onChange={(event) =>
                    updateConfig({
                      carriers: event.target.checked
                        ? [...config.carriers, carrier]
                        : config.carriers.filter((item) => item !== carrier),
                    })
                  }
                  data-testid={`shippo-carrier-${carrier.toLowerCase()}`}
                />{" "}
                {carrier}
              </label>
            ))}
          </div>
          <label className="block max-w-xs">
            <FieldLabel>Fallback shipping rate (USD)</FieldLabel>
            <input
              type="number"
              min="0"
              step="0.01"
              className={inputCls}
              value={config.fallback_rate}
              onChange={(event) =>
                updateConfig({ fallback_rate: event.target.value })
              }
              data-testid="shippo-fallback-rate"
            />
          </label>
          <Btn
            type="button"
            onClick={saveConfig}
            disabled={saving}
            data-testid="shippo-save-config-button"
          >
            {saving && <Loader2 size={15} className="animate-spin" />} Save
            shipping settings
          </Btn>
          <p
            className="text-xs text-zinc-500"
            data-testid="shippo-checkout-rate-status"
          >
            {config.checkout_rates_active
              ? "Live rates are active."
              : "Live rates are off; the configured fallback rate remains explicit at checkout."}
          </p>
        </div>
      </Card>

      <Card
        title="Package presets"
        description="Use mailer dimensions and empty packaging weight; set 0 when product weight is already packed."
      >
        <div className="space-y-4">
          <label className="block max-w-md">
            <FieldLabel>Preset name</FieldLabel>
            <input
              className={inputCls}
              value={preset.name}
              onChange={(event) =>
                setPreset({ ...preset, name: event.target.value })
              }
              data-testid="shippo-preset-name"
            />
          </label>
          {packageFields(preset, setPreset, "shippo-preset")}
          <Btn
            type="button"
            variant="secondary"
            onClick={savePreset}
            data-testid="shippo-save-preset-button"
          >
            Save package preset
          </Btn>
          <div
            className="divide-y divide-zinc-100"
            data-testid="shippo-presets-list"
          >
            {config.package_presets.map((entry) => (
              <div
                key={entry.id}
                className="py-3 flex justify-between gap-3 text-sm"
              >
                <span>
                  {entry.name} · {entry.length} × {entry.width} × {entry.height}{" "}
                  in · {entry.weight} lb
                </span>
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="text-zinc-600 underline"
                    onClick={() =>
                      updateConfig({ default_preset_id: entry.id })
                    }
                    data-testid={`shippo-default-preset-${entry.id}`}
                  >
                    {config.default_preset_id === entry.id
                      ? "Default"
                      : "Set default"}
                  </button>
                  <button
                    type="button"
                    className="text-red-600 underline"
                    onClick={async () => {
                      if (!window.confirm("Delete this package preset?"))
                        return;
                      await api.delete(`/admin/shippo/presets/${entry.id}`);
                      await load();
                    }}
                    data-testid={`shippo-delete-preset-${entry.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card
        title="Product shipping profile"
        description="Required for a live rate; use total packed weight when empty package weight is 0."
      >
        <div className="space-y-4">
          <label className="block max-w-md">
            <FieldLabel>Product</FieldLabel>
            <select
              className={inputCls}
              value={selectedProduct}
              onChange={(event) => {
                const product = products.find(
                  (item) => item.id === event.target.value,
                );
                setSelectedProduct(event.target.value);
                setProfile({
                  ...emptyPackage,
                  ...(product?.shippo_shipping || {}),
                  name: "",
                });
              }}
              data-testid="shippo-product-select"
            >
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </label>
          {packageFields(profile, setProfile, "shippo-product")}
          <Btn
            type="button"
            variant="secondary"
            onClick={saveProfile}
            data-testid="shippo-save-product-button"
          >
            <PackageCheck size={15} /> Save product shipping profile
          </Btn>
        </div>
      </Card>

      <Card
        title="Shippo webhook"
        description="Tracking events update matching orders only after secure validation."
      >
        {config.webhook_url ? (
          <div className="flex flex-wrap gap-2">
            <input
              readOnly
              className={`${inputCls} flex-1 min-w-[240px]`}
              value={config.webhook_url}
              data-testid="shippo-webhook-url"
            />
            <Btn
              type="button"
              variant="secondary"
              onClick={copyWebhook}
              data-testid="shippo-copy-webhook-button"
            >
              <Copy size={15} /> Copy
            </Btn>
          </div>
        ) : (
          <p
            className="text-sm text-zinc-500"
            data-testid="shippo-webhook-missing"
          >
            Add SHIPPO_WEBHOOK_TOKEN in protected server secrets to generate the
            webhook URL.
          </p>
        )}
        <p
          className="mt-3 text-xs text-zinc-500"
          data-testid="shippo-webhook-status"
        >
          {config.webhook_verified_at
            ? `Verified ${new Date(config.webhook_verified_at).toLocaleString()}`
            : "Awaiting a valid Shippo delivery."}
        </p>
      </Card>
    </div>
  );
}
